"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUtxoAPI = exports.calculateBalances = exports.getUtxosAmountValues = exports.getUtxoSetAssetAmountUtxos = exports.UtxoSet = exports.fetchUtxos = void 0;
const chain_1 = require("../chain");
const transaction_1 = require("../transaction");
const wallet_1 = require("../wallet");
const errors_1 = require("./errors");
const UtxoRequestLimit = 1024;
async function fetchUtxos(utxoApi, addresses, sourceChain, transactionId) {
    // use a set to avoid duplicates because getUtxos does not guarantee to provide unique
    // utxos between multiple calls. There could be some duplicates because of start/end indexes
    // or even if one transaction changes one of the utxos between two calls.
    const utxoSet = new Set();
    const utxos = [];
    let utxoResponse = sourceChain === undefined
        ? await utxoApi.getUTXOs(addresses, UtxoRequestLimit)
        : await utxoApi.getUTXOsFrom(addresses, sourceChain, UtxoRequestLimit);
    for (const data of utxoResponse.utxos) {
        const utxo = transaction_1.Utxo.parse(data);
        utxo.sourceChain = sourceChain;
        if (addUtxo(utxos, utxo, transactionId)) {
            utxoSet.add(utxo.getUniqueId());
        }
    }
    while (utxoResponse.numFetched === UtxoRequestLimit) {
        utxoResponse =
            sourceChain === undefined
                ? await utxoApi.getUTXOs(addresses, UtxoRequestLimit, utxoResponse.endIndex)
                : await utxoApi.getUTXOsFrom(addresses, sourceChain, UtxoRequestLimit, utxoResponse.endIndex);
        for (const data of utxoResponse.utxos) {
            const utxo = transaction_1.Utxo.parse(data);
            utxo.sourceChain = sourceChain;
            const key = utxo.getUniqueId();
            if (utxoSet.has(key)) {
                continue;
            }
            if (addUtxo(utxos, utxo, transactionId)) {
                utxoSet.add(key);
            }
        }
    }
    return utxos;
}
exports.fetchUtxos = fetchUtxos;
function addUtxo(utxos, utxo, transactionId) {
    if (typeof transactionId !== 'string') {
        utxos.push(utxo);
        return true;
    }
    if (transactionId === utxo.transactionId.value) {
        utxos.push(utxo);
        return true;
    }
    return false;
}
class UtxoSet {
    expectedSigners = [];
    selectedSigners = new Map();
    utxos;
    constructor(utxos) {
        this.utxos = utxos;
        for (const utxo of utxos) {
            this.expectedSigners.push(new ExpectedSigner(utxo.output.addresses, utxo.output.threshold));
        }
    }
    getUniqueAddresses() {
        const addresses = [];
        for (const utxo of this.utxos) {
            addresses.push(...utxo.output.addresses);
        }
        return transaction_1.Address.uniqueAddresses(addresses);
    }
    getSelectedSigners() {
        return this.selectedSigners.values();
    }
    addSigner(address) {
        const key = address.encodeHex();
        // already added avoid duplicate entries
        if (this.selectedSigners.has(key)) {
            return false;
        }
        // cannot sign this utxoSet
        if (!this.isValidSigner(address)) {
            return false;
        }
        this.selectedSigners.set(key, address);
        return true;
    }
    removeSigner(address) {
        const key = address.encodeHex();
        return this.selectedSigners.delete(key);
    }
    getMissingSignersAddresses() {
        const missing = [];
        for (const expectedSigner of this.expectedSigners) {
            const threshold = expectedSigner.threshold;
            const addresses = [];
            let selectedAmount = 0;
            for (const address of expectedSigner.addresses) {
                if (this.selectedSigners.has(address.encodeHex())) {
                    selectedAmount += 1;
                    continue;
                }
                addresses.push(address);
            }
            if (selectedAmount < threshold) {
                for (const address of addresses) {
                    if (address.matchesList(missing)) {
                        continue;
                    }
                    missing.push(address);
                }
            }
        }
        return missing;
    }
    isValidSigner(address) {
        for (const expectedSigner of this.expectedSigners) {
            if (address.matchesList(expectedSigner.addresses)) {
                return true;
            }
        }
        return false;
    }
}
exports.UtxoSet = UtxoSet;
class ExpectedSigner {
    addresses;
    threshold;
    constructor(addresses, threshold) {
        this.addresses = addresses;
        this.threshold = threshold;
    }
}
function getUtxoSetAssetAmountUtxos(utxoSet, assetId, amount, ignoredUtxos) {
    const utxos = [];
    if (amount === BigInt(0)) {
        return utxos;
    }
    const hasIgnoredUtxos = typeof ignoredUtxos !== 'undefined';
    let totalAmount = BigInt(0);
    for (const utxo of utxoSet) {
        if (utxo.assetId.value !== assetId || !('amount' in utxo.output)) {
            continue;
        }
        if (hasIgnoredUtxos) {
            for (const ignored of ignoredUtxos) {
                if (utxo.getUniqueId() === ignored.getUniqueId()) {
                    continue;
                }
            }
        }
        totalAmount += utxo.output.amount;
        utxos.push(utxo);
        if (totalAmount >= amount) {
            break;
        }
    }
    return utxos;
}
exports.getUtxoSetAssetAmountUtxos = getUtxoSetAssetAmountUtxos;
function getUtxosAmountValues(utxoSet, source) {
    const values = new Map();
    for (const utxo of utxoSet) {
        if (utxo.sourceChain !== source || !('amount' in utxo.output)) {
            continue;
        }
        let value = utxo.output.amount;
        const assetId = utxo.assetId.value;
        if (values.has(assetId)) {
            value += values.get(assetId);
        }
        values.set(assetId, value);
    }
    return values;
}
exports.getUtxosAmountValues = getUtxosAmountValues;
function calculateBalances(utxoSet, balances) {
    const values = getUtxosAmountValues(utxoSet);
    for (const [key, value] of values) {
        if (!balances.has(key)) {
            balances.set(key, new wallet_1.Balance());
        }
        balances.get(key).update(value);
    }
    for (const [key, balance] of balances) {
        // force all balances that no longer have a value from calculation to 0 in order to prevent desync
        if (!values.has(key) && balance.getValue() !== BigInt(0)) {
            // make sure to actually update so as to cast the event to potential listeners
            balance.update(BigInt(0));
        }
    }
}
exports.calculateBalances = calculateBalances;
function getUtxoAPI(provider, chain) {
    const vmId = chain.vm.id;
    if (vmId === chain_1.JVM_ID) {
        return provider.jvmApi;
    }
    else if (vmId === chain_1.PLATFORMVM_ID) {
        return provider.platformApi;
    }
    else if (vmId === chain_1.JEVM_ID) {
        return provider.jevmApi[chain.id];
    }
    throw new errors_1.WalletError(`unsupported vm id does not provide utxo api: ${vmId}`);
}
exports.getUtxoAPI = getUtxoAPI;
